// components/ResponsiveNavbar.jsx
import React from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  Box,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HomeIcon from "@mui/icons-material/Home";
import InfoIcon from "@mui/icons-material/Info";
import BuildIcon from "@mui/icons-material/Build";
import BusinessIcon from "@mui/icons-material/Business";
import PublicIcon from "@mui/icons-material/Public";
import { styled } from "@mui/system";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// Styled components
const LogoImage = styled("img")(({ theme, isMobile }) => ({
  width: isMobile ? 40 : 50,
  height: "auto",
  cursor: "pointer",
}));

const NavLinkButton = styled(Button)(({ theme }) => ({
  color: "#fff",
  textTransform: "none",
  fontSize: "20px",
  marginLeft: theme.spacing(2),
  "&:hover": {
    backgroundColor: "rgba(255,255,255,0.1)",
  },
}));

const drawerWidth = 240;

const iconMap = {
  Home: <HomeIcon />,
  About: <InfoIcon />,
  Services: <BuildIcon />,
  Business: <BusinessIcon />,
};

const navLinks = [
  { text: "Home", path: "/" },
  { text: "About", path: "/about" },
  { text: "Services", path: "/services" },

];

const businessOptions = [
  { text: "Medical Equipment Maintenance", path: "/medical-equipment-maintenance" },
  { text: "Medical Equipment Supplying", path: "/medical-equipment-supplying" },
  { text: "Hospital & Clinics Maintenance", path: "/hospital-clinics-maintenance" },
];

export default function ResponsiveNavbar() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [businessSubOpen, setBusinessSubOpen] = React.useState(false);
  const [langAnchorEl, setLangAnchorEl] = React.useState(null);

  const openLangMenu = Boolean(langAnchorEl);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
    // If closing drawer, also collapse submenu
    if (drawerOpen) {
      setBusinessSubOpen(false);
    }
  };

  const handleBusinessClick = () => {
    setBusinessSubOpen((prev) => !prev);
  };

  const handleLangClick = (event) => {
    setLangAnchorEl(event.currentTarget);
  };

  const handleLangClose = (lang) => {
    setLangAnchorEl(null);
    if (lang) {
      i18n.changeLanguage(lang === "Arabic" ? "ar" : "en");
    }
  };

  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile) {
      handleDrawerToggle();
    }
  };

  return (
    <>
      {/* AppBar */}
      <AppBar position="sticky" style={{ background:"rgb(206, 130, 56)"}} elevation={4}>
        <Toolbar sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {/* Logo */}
          <Box sx={{ display: "flex", alignItems: "center" , background:"white" ,borderRadius:"20%" ,m:1 }}>
            <LogoImage
           
              src="/image/Logo.png"
              alt="Logo"
              isMobile={isMobile}
              onClick={() => handleNavigate("/")}
            />
          </Box>

          {/* Desktop links */}
          {!isMobile && (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {navLinks.map((link) => {
                if (link.text === "Business") {
                  return (
                    <Box key={link.text} sx={{ position: "relative", marginLeft: theme.spacing(2) }}>
                      <NavLinkButton
                        aria-controls="business-menu"
                        aria-haspopup="true"
                        onClick={(e) => {
                          // you can open a drop‑down menu here
                          setLangAnchorEl(null); // close other
                          setBusinessSubOpen((prev) => !prev);
                        }}
                      >
                        {t(link.text)} {businessSubOpen ? <ExpandLessIcon sx={{ fontSize: "30px", color: "#fff" }} /> : <ExpandMoreIcon sx={{ fontSize: "20px", color: "#fff" }} />}
                      </NavLinkButton>
                      <Menu
                        id="business-menu"
                        anchorEl={langAnchorEl} // reuse anchor for language? Better to create separate anchor for business. But for simplicity: 
                        open={businessSubOpen}
                        onClose={() => setBusinessSubOpen(false)}
                        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                        transformOrigin={{ vertical: "top", horizontal: "left" }}
                      >
                        {businessOptions.map((opt) => (
                          <MenuItem key={opt.path} onClick={() => handleNavigate(opt.path)}>
                            {t(opt.text)}
                          </MenuItem>
                        ))}
                      </Menu>
                    </Box>
                  );
                }
                return (
                  <NavLinkButton key={link.text} onClick={() => handleNavigate(link.path)} >
                    {t(link.text)}
                  </NavLinkButton>
                );
              })}
            </Box>
          )}

          {/* Right icons */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton color="inherit" onClick={handleLangClick} size="large">
              <PublicIcon />
            </IconButton>
            <Menu
              anchorEl={langAnchorEl}
              open={openLangMenu}
              onClose={() => handleLangClose(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem onClick={() => handleLangClose("English")}>{t("English")}</MenuItem>
              <MenuItem onClick={() => handleLangClose("Arabic")}>{t("Arabic")}</MenuItem>
            </Menu>

            {isMobile && (
              <IconButton color="inherit" onClick={handleDrawerToggle} size="large">
                <MenuIcon />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            height: "100vh",
            width:"250px",
            backgroundColor: theme.palette.background.paper,
          },
        }}
      >
        <Box sx={{ width: "100%", padding: theme.spacing(4, 2) }}>
          <List>
            {navLinks.map((link) => {
              if (link.text === "Business") {
                return (
                  <React.Fragment key={link.text}>
                    <ListItem button onClick={handleBusinessClick}>
                      <ListItemIcon>{iconMap[link.text]}</ListItemIcon>
                      <ListItemText primary={t(link.text)} />
                      {businessSubOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </ListItem>
                    <Collapse in={businessSubOpen} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {businessOptions.map((opt) => (
                          <ListItem
                            key={opt.path}
                            button
                            sx={{ pl: 4 }}
                            onClick={() => handleNavigate(opt.path)}
                          >
                            <ListItemText primary={t(opt.text)} />
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  </React.Fragment>
                );
              }
              return (
                <ListItem
                  key={link.text}
                  button
                  onClick={() => handleNavigate(link.path)}
                >
                  <ListItemIcon>{iconMap[link.text]}</ListItemIcon>
                  <ListItemText primary={t(link.text)} />
                </ListItem>
              );
            })}
          </List>
        </Box>
      </Drawer>
    </>
  );
}
